/**
 * The Marketing tab's channel navigation (listing-marketing-hub US-3): Craigslist, Facebook
 * Marketplace and Other sites, each with a one-line status. A vertical rail beside the panel from md
 * up; stacked above it on a phone.
 */
import { cn } from '../../../lib/utils';

export type MarketingChannel = 'craigslist' | 'facebook' | 'other';

export interface ChannelRailItem {
  key: MarketingChannel;
  label: string;
  status: string;
  /** A tracked site with a posted record. */
  posted: boolean;
}

interface ChannelRailProps {
  items: ChannelRailItem[];
  active: MarketingChannel;
  onSelect: (channel: MarketingChannel) => void;
}

export function ChannelRail({ items, active, onSelect }: ChannelRailProps) {
  return (
    <nav aria-label="Marketing channels" className="w-full md:w-60 md:flex-none" data-testid="marketing-channel-rail">
      <ul className="flex flex-col gap-2">
        {items.map((item) => {
          const current = item.key === active;
          return (
            <li key={item.key}>
              <button
                type="button"
                aria-current={current ? 'true' : undefined}
                onClick={() => onSelect(item.key)}
                data-testid={`marketing-channel-${item.key}`}
                className={cn(
                  'flex min-h-[56px] w-full flex-col justify-center rounded-lg border px-3.5 py-2 text-left transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  current
                    ? 'border-primary bg-primary/10 shadow-[inset_3px_0_0_hsl(var(--primary))]'
                    : 'border-border bg-card hover:bg-accent',
                )}
              >
                <span className="text-sm font-semibold text-foreground">{item.label}</span>
                <span
                  className={cn('text-xs', item.posted ? 'text-success' : 'text-muted-foreground')}
                  data-testid={`marketing-channel-${item.key}-status`}
                >
                  {item.status}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
